import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import { UiAccountsControllerService } from 'src/app/cc-api/services';
import { FacetsPage } from 'src/app/facets/facets-page/facets-page.component';
import { NbPopoverDirective } from '@nebular/theme';
import { whiteSpaceInputPattern } from '../../constants/inputPatterns';
import { vcsAccountIcons } from './accounts-constants';
import { TokenExpiryService } from 'src/app/services/token-expiry.service';
import debounce from 'lodash/debounce';

@Component({
  selector: 'app-create-vcs-account-integration',
  templateUrl: './create-vcs-account-integration.component.html',
  styleUrls: ['./create-vcs-account-integration.component.scss'],
})
export class CreateVcsAccountIntegrationComponent extends FacetsPage {
  @Output() afterSaveAccount: EventEmitter<any> = new EventEmitter<any>();
  @Output() afterChangeIntegration: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective; //copy popover
  @Input() passedRef: any;
  @Input() vcsAccountType: any;
  @Input() allowCancel: boolean = true;
  vcsAccountIcons = vcsAccountIcons;
  accountForm: UntypedFormGroup = this.fb.group({
    accountName: [
      '',
      [Validators.required, Validators.pattern('^[a-z0-9-]+$')],
    ],
    hostedOn: ['github.com'],
    accessToken: [''],
    selectedOrgName: new UntypedFormControl(''),
  });
  readonly ACCOUNTS_WRITE = 'ACCOUNTS_WRITE';
  alertTitle: string;
  alertType: string;
  alertDescription: string;
  creatingVcsAccount: boolean = false;
  creationError: boolean = false;
  enableStepTwo: boolean = false;
  vcsAccounts = [
    { type: 'GITHUB', label: 'Git' },
    { type: 'GITLAB', label: 'GitLab' },
    { type: 'BITBUCKET', label: 'Bitbucket' },
  ];

  loadingOrgNames: boolean = false;
  orgNames: string[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private accountControllerService: UiAccountsControllerService,
    private fb: UntypedFormBuilder,
    private tokenExpiryService: TokenExpiryService,
  ) {
    super(activatedRoute);
  }

  get accountName() {
    return this.accountForm.get('accountName');
  }

  get accessToken() {
    return this.accountForm.get('accessToken');
  }

  get hostedOn() {
    return this.accountForm.get('hostedOn');
  }

  get selectedOrgName() {
    return this.accountForm.get('selectedOrgName');
  }

  OnPageLoad(p: Params): void {
    this.alertDescription = '';
    this.enableStepTwo = false;
    this.creationError = false;
    if (this.vcsAccountType === 'GITHUB') {
      this.hostedOn.addValidators([Validators.required]);
    }
    if (this.vcsAccountType === 'GITLAB') {
      this.hostedOn.setValue('gitlab.com');
    }
  }

  /*
    * Clicking on Next button will have 2 conditions.
    1. When step 2 is not shown to the user, then show step 2 with token generation instruction and input field
    2. When step 2 is shown to the user, submit the account and show success or error message to the user

    * While saving the account input fields needs to be disabled and they should stay disabled when success message is shown
  */
  showNextStep() {
    if (!this.enableStepTwo) {
      this.enableStepTwo = true;
      this.accountName?.disable();
      this.hostedOn.disable();
      this.cleanHostedOn();
      this.accessToken.addValidators([
        Validators.required,
        Validators.pattern(whiteSpaceInputPattern),
      ]);
      triggerAnalyticsEvent(`ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_NEXT`);
    } else {
      this.submitVcsAccount();
    }
  }

  submitVcsAccount() {
    this.creatingVcsAccount = true;
    this.creationError = false;
    this.alertDescription = '';
    this.alertTitle = '';
    this.accessToken?.disable();
    this.selectedOrgName?.disable();
    let accountName = this.accountName?.value?.trim() || '';
    let token = this.accessToken?.value?.trim() || '';
    let enterpriseHost = this.hostedOn?.value;
    let selectedOrgName = this.selectedOrgName?.value?.trim();
    let accountParams: any = {
      accessToken: token,
      name: accountName,
      accountType: 'VERSION_CONTROL',
      provider: this.vcsAccountType,
      orgName: selectedOrgName === 'PERSONAL' ? null : selectedOrgName,
    };
    let httpRequest = null;
    if (this.vcsAccountType === 'GITHUB') {
      accountParams.enterpriseHostName = enterpriseHost;
      httpRequest =
        this.accountControllerService.createGitHubAccountUsingPOST(
          accountParams,
        );
    } else if (this.vcsAccountType === 'GITLAB') {
      accountParams.enterpriseHostName = enterpriseHost;
      httpRequest =
        this.accountControllerService.createGitLabAccountUsingPOST(
          accountParams,
        );
    } else if (this.vcsAccountType === 'BITBUCKET') {
      httpRequest =
        this.accountControllerService.createBitBucketAccountUsingPOST(
          accountParams,
        );
    }
    httpRequest.subscribe(
      (account) => {
        this.creatingVcsAccount = false;
        this.onAccountCreateSuccess(account);
        triggerAnalyticsEvent(
          `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_SUCCESSFUL`,
          {
            accountName: this.accountName?.value,
            accountType: this.vcsAccountType,
          },
        );
      },
      (error) => {
        this.creatingVcsAccount = false;
        this.onAccountCreateFailure(error);
        triggerAnalyticsEvent(`ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_ERROR`);
      },
    );
  }

  onClickHandleCancel(ref: any) {
    triggerAnalyticsEvent(`ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_CANCEL`);
    ref.close();
  }

  async onAccountCreateSuccess(account) {
    this.alertType = 'success';
    this.alertTitle = `Success! Your ${account?.name} account is now linked with Facets Cloud`;
    this.alertDescription =
      "You're all set to harness the full power of our platform.";
    this.afterSaveAccount.emit(account);
    await this.tokenExpiryService.extractExpiringAndExpiredVCSAccounts();
  }

  onAccountCreateFailure(error) {
    this.resetInputFields();
    this.creationError = true;
    this.alertType = 'danger';
    this.alertTitle = `Account linking failed`;
    this.alertDescription =
      error?.error?.message ||
      'Please re-enter the correct token and try again.';
  }

  get isDisabled() {
    if (this.vcsAccountType === 'GITHUB') {
      return !this.hostedOn?.value?.length || !this.accountName?.value?.length;
    } else if (this.vcsAccountType === 'GITLAB') {
      return !this.hostedOn?.value?.length || !this.accountName?.value?.length;
    }
    return false;
  }

  cleanHostedOn() {
    this.hostedOn.setValue(
      this.hostedOn?.value
        ?.trim()
        ?.replace(/^https?:\/\//, '')
        ?.replace(/\/$/, '') || 'gitlab.com',
    );
  }

  generateTokenUrl() {
    triggerAnalyticsEvent(`ORG_SETTINGS_ACCOUNT_MANAGEMENT_GENERATE_TOKEN`, {
      accountType: this.vcsAccountType,
    });
    const tokenLinkMap = {
      GITHUB: `https://${this.hostedOn?.value}/settings/tokens/new?description=${this.accountName?.value}&scopes=repo,read:packages,read:org,workflow,read:user`,
      GITLAB: `https://${
        this.hostedOn?.value || 'gitlab.com'
      }/-/user_settings/personal_access_tokens?name=${
        this.accountName?.value
      }&scopes=api,read_user,read_registry,create_runner`,
      BITBUCKET:
        'https://support.atlassian.com/bitbucket-cloud/docs/app-passwords/',
    };
    window.open(tokenLinkMap[this.vcsAccountType], '_blank');
  }

  onChangePAT = debounce((event) => {
    if (typeof event === 'string' && event?.length) {
      this.selectedOrgName.reset();
      this.orgNames = [];
      this.getOrgsBasedOnPAT();
    }
  }, 500);

  getOrgsBasedOnPAT() {
    this.loadingOrgNames = true;
    this.accountControllerService
      .getVCSOrganisationsUsingPOST({
        vcsType: this.vcsAccountType,
        accessToken: this.accessToken?.value,
        enterpriseHostName:
          this.hostedOn?.value?.trim() === 'github.com'
            ? null
            : this.hostedOn?.value,
      })
      .subscribe(
        (response) => {
          this.loadingOrgNames = false;
          this.orgNames = ['PERSONAL', ...response];
          this.selectedOrgName.setValue('PERSONAL');
        },
        () => {
          this.loadingOrgNames = false;
        },
      );
  }

  resetInputFields() {
    this.accountName.enable();
    this.hostedOn.enable();
    this.accessToken.enable();
    this.selectedOrgName.enable();
  }

  resetInputs() {
    this.resetInputFields();
    this.accessToken.clearValidators();
    this.accessToken.updateValueAndValidity();
    this.accessToken.markAsUntouched();

    this.selectedOrgName.clearValidators();
    this.selectedOrgName.updateValueAndValidity();
    this.selectedOrgName.markAsUntouched();

    this.enableStepTwo = false;
    this.alertDescription = '';
    this.alertTitle = '';
    this.creationError = false;
  }

  changeIntegrationMethod() {
    this.afterChangeIntegration.emit('APP');
  }
}
